import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import * as z from "zod/v4";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ky, { HTTPError } from "ky";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { env } from "@/env";
import { saveTokens } from "@/lib/http";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { cn } from "@/lib/utils";

const verifySearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/auth/verify")({
  component: RouteComponent,
  validateSearch: zodValidator(verifySearchSchema),
  staticData: {
    title: "Verify Account",
  },
});

const REDIRECT_DELAY_MS = 5000;

type VerifyEmailResponse = {
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
};

type VerifyStatus = "loading" | "success" | "error";

async function verifyEmail(token: string): Promise<VerifyEmailResponse> {
  const res = await ky.get("api/v1/register/verify-email", {
    baseUrl: env.VITE_AUTH_API_URL,
    searchParams: { token },
  });
  return res.json<VerifyEmailResponse>();
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { token } = Route.useSearch();
  const verifyRequestId = useRef(0);
  const [status, setStatus] = useState<VerifyStatus>(() => {
    if (!token) return "error";
    return "loading";
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(
    "Your account is verified successfully",
  );
  const [redirectCountdown, setRedirectCountdown] = useState(
    REDIRECT_DELAY_MS / 1000,
  );

  useEffect(() => {
    if (!token) return;

    const requestId = ++verifyRequestId.current;

    void (async () => {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const data = await verifyEmail(token);
        if (requestId !== verifyRequestId.current) return;

        saveTokens({
          access: data.access_token,
          refresh: data.refresh_token,
        });

        setSuccessMessage(getSuccessTitle(data.message));
        setStatus("success");
      } catch (error) {
        if (requestId !== verifyRequestId.current) return;

        if (error instanceof HTTPError) {
          const data = await error.response
            .json<{ detail?: string }>()
            .catch(() => null);
          if (typeof data?.detail === "string") {
            setErrorMessage(data.detail);
            setStatus("error");
            return;
          }
        }

        setErrorMessage(
          error instanceof Error && error.message
            ? error.message
            : "We could not verify your account. The link may have expired.",
        );
        setStatus("error");
      }
    })();
  }, [token]);

  useEffect(() => {
    if (status !== "success") return;

    setRedirectCountdown(REDIRECT_DELAY_MS / 1000);

    const interval = window.setInterval(() => {
      setRedirectCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    const timeout = window.setTimeout(() => {
      void queryClient.invalidateQueries({
        queryKey: userInfoQueryOptions.queryKey,
      });
      navigate({ to: "/pricing", replace: true });
    }, REDIRECT_DELAY_MS);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [navigate, queryClient, status]);

  if (!token) {
    return (
      <VerifyPageShell>
        <VerifyStatusMessage
          title="Invalid verification link"
          description="This link is missing a verification token. Please use the link from your email or request a new one."
        />
        <Link
          to="/auth/login"
          className="text-sm font-medium text-[#dd2590] hover:underline"
        >
          Back to Log in
        </Link>
      </VerifyPageShell>
    );
  }

  if (status === "loading") {
    return (
      <VerifyPageShell>
        <Loader2 className="size-10 animate-spin text-[#dd2590]" />
        <p className="text-sm text-[#535862] dark:text-muted-foreground">
          Verifying your account...
        </p>
      </VerifyPageShell>
    );
  }

  if (status === "error") {
    return (
      <VerifyPageShell>
        <VerifyStatusMessage
          title="Verification failed"
          description={
            errorMessage ??
            "We could not verify your account. The link may have expired."
          }
        />
        <Link
          to="/auth/login"
          className="text-sm font-medium text-[#dd2590] hover:underline"
        >
          Back to Log in
        </Link>
      </VerifyPageShell>
    );
  }

  return (
    <VerifyPageShell className="gap-5">
      <img
        src="/images/verify-illustration.png"
        alt=""
        width={417}
        height={321}
        className="h-[321px] w-[417px] max-w-full object-contain"
      />

      <div className="flex max-w-[490px] flex-col gap-2 text-center">
        <h1 className="text-lg leading-7 font-semibold text-[#181d27] dark:text-foreground">
          {successMessage}
        </h1>
        <p className="text-sm leading-5 text-[#535862] dark:text-muted-foreground">
          You will be automatically directed to{" "}
          <span className="font-medium">Meeting Guard</span>
          {redirectCountdown > 0 ? ` in ${redirectCountdown}s` : "..."}
        </p>
      </div>
    </VerifyPageShell>
  );
}

function VerifyPageShell({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white px-6 font-[Manrope,system-ui,sans-serif] dark:bg-background">
      <div
        className={cn(
          "flex w-full max-w-[500px] flex-col items-center gap-5 text-center",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

function getSuccessTitle(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes("already verified")) {
    return "Your account is already verified";
  }
  return "Your account is verified successfully";
}

function VerifyStatusMessage({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex max-w-[490px] flex-col gap-2">
      <h1 className="text-lg leading-7 font-semibold text-[#181d27] dark:text-foreground">
        {title}
      </h1>
      <p className="text-sm leading-5 text-[#535862] dark:text-muted-foreground">
        {description}
      </p>
    </div>
  );
}
